import React, { useEffect, useState, useRef } from 'react';
import { Layout, List, Avatar, Badge, Input, Spin, message as antMessage } from 'antd';
import moment from 'moment';
// import './ChatApp.css'; // Optional: include custom styles if needed

const { Sider, Content } = Layout;

function ChatApp() {
  const [conversations, setConversations] = useState([]); // Array of { partner, messages, unreadCount }
  const [selectedConversation, setSelectedConversation] = useState(null); // The partner object for the active conversation
  const [messages, setMessages] = useState([]); // Messages for the selected conversation
  const [messageInput, setMessageInput] = useState('');
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Retrieve current user from localStorage
  const currentUser = JSON.parse(localStorage.getItem('chat_user'));

  // Scroll to bottom smoothly when new messages arrive
  useEffect(() => {
    const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };
    
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  // Modify the scroll behavior
  useEffect(() => {
    const messageContainer = document.querySelector('.messages-container');
    if (messageContainer && messages.length > 0) {
      messageContainer.scrollTop = messageContainer.scrollHeight;
    }
  }, [messages]);

  // Fetch conversation list (grouped by partner) from your backend
  const fetchConversations = async () => {
    if (!currentUser) return;
    try {
      setLoadingConversations(true);
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_CHAT_APP_BACKEND_URL;
      const response = await fetch(`${apiUrl}/auth/messages/${currentUser.email}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch conversations');
      }
      const data = await response.json();
      // data is expected to be an object: { userId, conversations: { <partnerId>: { partner, messages } } }
      const convArray = Object.values(data.conversations || {});
      // Sort conversations by the timestamp of the last message (newest first)
      convArray.sort((a, b) => {
        const aLast = a.messages[0]?.createdAt || 0;
        const bLast = b.messages[0]?.createdAt || 0;
        return new Date(bLast) - new Date(aLast);
      });
      setConversations(convArray);
    } catch (err) {
      antMessage.error(err.message);
    } finally {
      setLoadingConversations(false);
    }
  };

  // Fetch detailed conversation messages using the get-conversation endpoint
  const fetchConversationMessages = async (partnerEmail) => {
    if (!currentUser) return;
    try {
      setLoadingMessages(true);
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_CHAT_APP_BACKEND_URL;
      const response = await fetch(`${apiUrl}/auth/get-conversation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          userEmail: currentUser.email,
          otherUserEmail: partnerEmail,
        }),
      });
      if (!response.ok) {
        throw new Error('Failed to fetch conversation messages');
      }
      const data = await response.json();
      // data.conversation.messages contains the conversation messages
      setMessages(data.conversation.messages || []);
    } catch (err) {
      antMessage.error(err.message);
    } finally {
      setLoadingMessages(false);
    }
  };

  // WebSocket connection for real-time messaging
  const connectWebSocket = () => {
    if (!currentUser) {
      antMessage.error('Please login to use chat');
      return;
    }
    const wsUrl = process.env.REACT_APP_CHAT_WS_URL;
    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onopen = () => {
      console.log('WebSocket Connected');
      setWsConnected(true);
      // Register current user with the server
      wsRef.current.send(JSON.stringify({
        type: 'register',
        email: currentUser.email,
      }));
    };

    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('WebSocket received:', data);

      switch (data.type) {
        case 'connection_established':
          console.log('Connection confirmed:', data.message);
          break;
        case 'registered':
          localStorage.setItem('ws_user_id', data.userId);
          setIsInitialized(true);
          break;
        case 'chat message':
          // Build incoming message object
          const incomingMessage = {
            id: data.id,
            content: data.content,
            senderId: data.senderId,
            receiverId: data.receiverId,
            timestamp: data.createdAt || new Date().toISOString(),
            sender: data.sender,
          };
          // If the incoming message belongs to the currently selected conversation, update the chat window
          if (
            selectedConversation &&
            (incomingMessage.senderId === selectedConversation.id ||
              incomingMessage.receiverId === selectedConversation.id)
          ) {
            setMessages(prev => [...prev, incomingMessage]);
          }
          // Update conversation list: find the conversation by partner id and update its messages and unread count
          setConversations(prev => {
            let updated = [...prev];
            // Determine partner id based on sender and receiver
            const partnerId = incomingMessage.senderId === currentUser.id 
              ? incomingMessage.receiverId 
              : incomingMessage.senderId;
            const index = updated.findIndex(conv => conv.partner.id === partnerId);
            if (index !== -1) {
              const conv = updated[index];
              // Prepend new message (assuming the messages are ordered newest-first in the sidebar preview)
              conv.messages.unshift(incomingMessage);
              // If this conversation is not active, increment unread count
              if (!selectedConversation || selectedConversation.id !== partnerId) {
                conv.unreadCount = conv.unreadCount ? conv.unreadCount + 1 : 1;
              }
              // Move updated conversation to the top
              updated.splice(index, 1);
              return [{ ...conv }, ...updated];
            } else {
              // If conversation does not exist, create a new one
              const newConv = {
                partner: incomingMessage.senderId === currentUser.id ? data.receiver : data.sender,
                messages: [incomingMessage],
                unreadCount: (!selectedConversation || selectedConversation.id !== (incomingMessage.senderId === currentUser.id ? data.receiver.id : data.sender.id)) ? 1 : 0,
              };
              return [newConv, ...updated];
            }
          });
          break;
        case 'message sent':
          // Optionally update pending messages to "sent" if you track that status
          break;
        case 'error':
          antMessage.error(data.message);
          break;
        default:
          break;
      }
    };

    wsRef.current.onerror = (error) => {
      console.error('WebSocket error:', error);
      setWsConnected(false);
    };

    wsRef.current.onclose = () => {
      console.log('WebSocket disconnected');
      setWsConnected(false);
      // Reconnect after a short delay
      setTimeout(connectWebSocket, 3000);
    };
  };

  // Send a message via WebSocket
  const sendMessage = (content) => {
    if (!content.trim() || !wsRef.current || !wsConnected || !selectedConversation) return;
    const messageData = {
      type: 'chat message',
      senderId: currentUser.id,
      receiverId: selectedConversation.id,
      content: content.trim(),
    };
    try {
      const tempMessage = {
        id: Date.now(),
        content: content.trim(),
        senderId: currentUser.id,
        receiverId: selectedConversation.id,
        timestamp: new Date().toISOString(),
        status: 'pending',
        sender: currentUser,
      };
      setMessages(prev => [...prev, tempMessage]);
      wsRef.current.send(JSON.stringify(messageData));
      setMessageInput('');
    } catch (err) {
      antMessage.error('Failed to send message');
    }
  };

  // When a conversation in the sidebar is clicked, mark it as selected, clear its unread count, and load its messages
  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation.partner);
    // Clear unread count for this conversation
    setConversations(prev =>
      prev.map(conv => {
        if (conv.partner.id === conversation.partner.id) {
          return { ...conv, unreadCount: 0 };
        }
        return conv;
      })
    );
    // Fetch detailed conversation messages
    fetchConversationMessages(conversation.partner.email);
  };

  // Helper function to format timestamp
  const formatMessageTime = (timestamp) => {
    const messageDate = moment(timestamp);
    const now = moment();

    if (messageDate.isSame(now, 'day')) {
      // If message is from today, show only time
      return messageDate.format('h:mm A');
    } else if (messageDate.isSame(now.subtract(1, 'day'), 'day')) {
      // If message is from yesterday
      return 'Yesterday ' + messageDate.format('h:mm A');
    } else if (messageDate.isSame(now, 'week')) {
      // If message is from this week
      return messageDate.format('ddd h:mm A');
    } else {
      // For older messages
      return messageDate.format('MMM D, h:mm A');
    }
  };

  // On component mount, fetch the conversation list and connect WebSocket
  useEffect(() => {
    fetchConversations();
    connectWebSocket();

    // Optionally refresh conversation list periodically (e.g. every 60 seconds)
    const interval = setInterval(fetchConversations, 60000);
    return () => {
      clearInterval(interval);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  return (
    <Layout className="h-[calc(90vh-64px)] mt-16"> {/* Fixed height for chat container */}
      {/* Sidebar */}
      <Sider 
        width={300} 
        className="bg-white border-r border-gray-200"
        style={{ 
          height: '100%',
          overflow: 'hidden'
        }}
      >
        {/* Sidebar Header */}
        <div className="p-3 border-b border-gray-200 bg-white">
          <h2 className="text-lg font-semibold">Messages</h2>
          <div className="flex items-center mt-1 text-sm text-gray-500">
            <div className={`w-2 h-2 rounded-full mr-2 ${wsConnected ? 'bg-green-500' : 'bg-red-500'}`} />
            {wsConnected ? 'Connected' : 'Disconnected'}
          </div>
        </div>

        {/* Conversations List */}
        <div 
          className="overflow-y-auto custom-scrollbar bg-white"
          style={{ 
            height: 'calc(100% - 65px)',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',

          }}
        >
          {loadingConversations ? (
            <div className="flex justify-center p-4">
              <Spin />
            </div>
          ) : (
            <List
              className="divide-y divide-gray-200"
              dataSource={conversations}
              renderItem={conversation => (
                <div
                  onClick={() => handleSelectConversation(conversation)}
                  className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                    selectedConversation?.id === conversation.partner.id ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Badge count={conversation.unreadCount} offset={[-2, 2]}>
                      <Avatar 
                        size={40} 
                        src={conversation.partner.avatarUrl}
                        className="bg-blue-500"
                      >
                        {conversation.partner.username?.[0]?.toUpperCase()}
                      </Avatar>
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {conversation.partner.username}
                        </p>
                        {conversation.messages[0] && (
                          <p className="text-xs text-gray-500">
                            {moment(conversation.messages[0].createdAt).fromNow()}
                          </p>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 truncate">
                        {conversation.messages[0]?.content || 'No messages yet'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            />
          )}
        </div>
      </Sider>

      {/* Main Chat Area */}
      <Content className="flex flex-col bg-gray-50 relative"> {/* Added relative positioning */}
        {selectedConversation ? (
          <>
            {/* Chat Header - Fixed at top */}
            <div className="p-3 bg-white border-b border-gray-200 shadow-sm">
              <div className="flex items-center space-x-3">
                <Avatar 
                  size={36} 
                  src={selectedConversation.avatarUrl}
                  className="bg-blue-500"
                >
                  {selectedConversation.username?.[0]?.toUpperCase()}
                </Avatar>
                <div>
                  <h2 className="text-base font-semibold">{selectedConversation.username}</h2>
                  <p className="text-xs text-gray-500">
                    {wsConnected ? 'Online' : 'Offline'}
                  </p>
                </div>
              </div>
            </div>

            {/* Messages Area - Scrollable container */}
            <div 
              className="messages-container flex-1 overflow-y-auto p-4 space-y-3"
              style={{ 
                height: 'calc(100% - 120px)', // Adjust for header and input heights
                overflowY: 'auto',
                scrollBehavior: 'smooth',
                paddingBottom: '60px' // Add padding to show messages above input
              }}
            >
              <div className="flex flex-col space-y-3">
                {messages
                  .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
                  .map((message, index) => (
                    <div
                      key={index}
                      className={`flex ${message.senderId === currentUser.id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-lg px-3 py-2 ${
                          message.senderId === currentUser.id
                            ? 'bg-blue-500 text-white'
                            : 'bg-white text-gray-900'
                        } shadow-sm`}
                      >
                        <p className="text-sm">{message.content}</p>
                        <div className="flex items-center justify-end space-x-1 mt-1">
                          <span className="text-[10px] opacity-75">
                            {formatMessageTime(message.timestamp)}
                          </span>
                          {message.senderId === currentUser.id && (
                            <span className="text-[10px]">
                              {message.status === 'pending' ? '⌛' : '✓'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Message Input - Fixed at bottom */}
            <div className="absolute bottom-0 left-0 right-0 p-3 bg-white border-t border-gray-200">
              <Input.Search
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                onSearch={sendMessage}
                enterButton="Send"
                placeholder="Type a message..."
                className="rounded-lg"
                disabled={!wsConnected}
              />
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="text-lg font-medium">Select a conversation to start chatting</p>
          </div>
        )}
      </Content>
    </Layout>
  );
}

// Add these styles to your CSS
const styles = `
  .messages-container::-webkit-scrollbar {
    width: 6px;
  }

  .messages-container::-webkit-scrollbar-track {
    background: transparent;
  }

  .messages-container::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.1);
    border-radius: 3px;
  }

  .messages-container::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.2);
  }

  .messages-container {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.1) transparent;
  }
`;

// Add the styles to the document
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default ChatApp;
